import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackingApi } from '@/api/endpoints/tracking';
import {
  getOrCreateVisitorId,
  hasGuestVisitBeenSent,
  markGuestVisitSent,
  readAttribution,
} from './attribution';
import { track } from './tracker';

/** page_view на каждую смену пути, включая гостей (TRK-3). */
export function useRouteTelemetry(isAuthenticated: boolean): void {
  const { pathname } = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    track('page_view', { metadata: { is_authenticated: isAuthenticated } });
  }, [pathname, isAuthenticated]);
}

/** Гостевой визит — один раз на вкладку, пока пользователь не вошёл (TRK-3). */
export function useGuestVisit(isAuthenticated: boolean): void {
  useEffect(() => {
    if (isAuthenticated || hasGuestVisitBeenSent()) return;
    markGuestVisitSent();
    const attribution = readAttribution() ?? {};
    trackingApi
      .guestVisit({
        visitor_id: getOrCreateVisitorId(),
        page: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        source: 'app',
        ...attribution,
      })
      .catch(() => {});
  }, [isAuthenticated]);
}

const HEARTBEAT_INTERVAL_MS = 60_000;
const ACTIVITY_EVENTS = ['mousemove', 'click', 'scroll', 'keydown', 'touchstart'] as const;

/** Heartbeat раз в 60 с только при реальной активности и видимой вкладке (TRK-6). */
export function useActivityHeartbeat(isAuthenticated: boolean): void {
  const hadActivity = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const markActivity = () => {
      hadActivity.current = true;
    };
    const send = () => {
      if (document.visibilityState === 'visible' && hadActivity.current) {
        hadActivity.current = false;
        trackingApi.heartbeat().catch(() => {});
      }
    };

    for (const event of ACTIVITY_EVENTS)
      window.addEventListener(event, markActivity, { passive: true });
    hadActivity.current = true;
    send();
    const interval = setInterval(send, HEARTBEAT_INTERVAL_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, markActivity);
      clearInterval(interval);
    };
  }, [isAuthenticated]);
}
